import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetPerformanceLiquidity } from 'services/calculations/financialPerformance/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

const hasAllPerformanceLiquidityFields = (requestData: Record<string, unknown>) => {
  return Object.values(requestData).every((value) => value !== undefined && value !== null);
};

export const usePerformanceLiquidity = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getPerformanceLiquidity = useGetPerformanceLiquidity();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [performanceLiquidityFiled] = observedFields.performanceLiquidity.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (requestData, unit) => {
        if (!hasAllPerformanceLiquidityFields(requestData)) return;

        const params = {
          unit,
          businessPlanId,
          requestData,
        };

        try {
          const { currentRatio, quickRatio } = await getPerformanceLiquidity.mutateAsync(params);

          setValue(performanceLiquidityFiled, [currentRatio, quickRatio]);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const performanceLiquidityHandler = useCallback(
    async (content: IFormContentTypes['performanceLiquidity']) => {
      if (!canAccess) return;

      const [totalCurrentAssetData, totalAssetData, totalCurrentLiability, inventoryData, operationUnit] = content;
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      const performanceLiquidityData = {
        totalCurrentAssetData,
        totalAssetData,
        totalCurrentLiability,
        inventoryData,
      };

      debouncedUpdateFunction(performanceLiquidityData, unit);
    },

    [debouncedUpdateFunction],
  );

  return performanceLiquidityHandler;
};
